import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const COLORS = {
  sensory: '#2493A2',
  integration: '#57BDA2',
  symbolic: '#DCB13C',
  capstone: '#E85D75',
};

export function UrgencyAmplifier() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svgRef.current) return;

    svg.selectAll('*').remove();

    const H = 160;
    const midY = H / 2;

    // -- Background subtle grid lines --
    const defs = svg.append('defs');

    // Glow filter for the "NOW" pulse
    const glowFilter = defs.append('filter').attr('id', 'urgency-glow');
    glowFilter.append('feGaussianBlur').attr('stdDeviation', 4).attr('result', 'blur');
    const merge = glowFilter.append('feMerge');
    merge.append('feMergeNode').attr('in', 'blur');
    merge.append('feMergeNode').attr('in', 'SourceGraphic');

    // -- Amplifier triangles (static) --
    const ampGroup = svg.append('g').attr('class', 'amplifiers');
    const trianglePositions = [110, 155, 200];
    const triangleSizes = [14, 18, 22];

    trianglePositions.forEach((cx, i) => {
      const s = triangleSizes[i];
      ampGroup.append('polygon')
        .attr('points', `${cx - s * 0.6},${midY - s} ${cx + s * 0.8},${midY} ${cx - s * 0.6},${midY + s}`)
        .attr('fill', 'none')
        .attr('stroke', COLORS.symbolic)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.5);

      // "x" multiplier labels
      if (i < trianglePositions.length - 1) {
        ampGroup.append('text')
          .attr('x', cx + triangleSizes[i] + 4)
          .attr('y', midY + 4)
          .attr('fill', 'var(--color-text-muted)')
          .attr('font-size', '8px')
          .text(`x${(i + 2)}`);
      }
    });

    // "threat bias" label
    ampGroup.append('text')
      .attr('x', 155)
      .attr('y', midY + 42)
      .attr('text-anchor', 'middle')
      .attr('fill', 'var(--color-text-muted)')
      .attr('font-size', '8px')
      .attr('font-style', 'italic')
      .text('threat bias');

    // -- "signal" label on the left --
    svg.append('text')
      .attr('x', 20)
      .attr('y', midY - 22)
      .attr('fill', 'var(--color-text-muted)')
      .attr('font-size', '9px')
      .attr('text-anchor', 'middle')
      .text('signal');

    // -- "NOW" label on the right (static backing) --
    const nowGroup = svg.append('g').attr('transform', `translate(275, ${midY})`);

    // Pulsing outer ring
    const pulseCircle = nowGroup.append('circle')
      .attr('r', 28)
      .attr('fill', 'none')
      .attr('stroke', COLORS.capstone)
      .attr('stroke-width', 2)
      .attr('opacity', 0.3);

    // Pulse animation on the ring
    function pulseRing() {
      pulseCircle
        .attr('r', 28)
        .attr('opacity', 0.4)
        .transition()
        .duration(800)
        .ease(d3.easeSinInOut)
        .attr('r', 36)
        .attr('opacity', 0)
        .on('end', pulseRing);
    }
    pulseRing();

    // Static inner circle for "NOW"
    nowGroup.append('circle')
      .attr('r', 24)
      .attr('fill', COLORS.capstone)
      .attr('opacity', 0.15);

    nowGroup.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', COLORS.capstone)
      .attr('font-size', '14px')
      .attr('font-weight', '700')
      .text('NOW');

    // -- Animated signal traveling left to right --
    const signalGroup = svg.append('g');

    function animateSignal() {
      const signal = signalGroup.append('circle')
        .attr('cx', 20)
        .attr('cy', midY)
        .attr('r', 4)
        .attr('fill', COLORS.sensory)
        .attr('opacity', 0.9);

      signal.transition()
        .duration(600)
        .ease(d3.easeLinear)
        .attr('cx', 110)
        .attr('r', 6)
        .attr('fill', COLORS.integration)
        .transition()
        .duration(500)
        .ease(d3.easeLinear)
        .attr('cx', 200)
        .attr('r', 10)
        .attr('fill', COLORS.symbolic)
        .transition()
        .duration(400)
        .ease(d3.easeQuadIn)
        .attr('cx', 260)
        .attr('r', 16)
        .attr('fill', COLORS.capstone)
        .attr('opacity', 0)
        .remove();
    }

    // Start first animation, then repeat
    animateSignal();
    const interval = setInterval(animateSignal, 2200);

    return () => {
      clearInterval(interval);
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-bolt" /> Urgency Amplifier
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 160" />
      <p className="viz-container__caption">Modern urgency often misfires.</p>
    </div>
  );
}
